<template>
  <div class="container">
    <v-breadcrumbs :items="breadcrumbItems"/>

    <v-card class="mb-12">
      <page-header title="Renew an event" compact />

      <v-card-text>
        <v-form ref="form">
          <div v-if="!user">
            <h3>Login to access account</h3>
            <p>Please login first to access your events.</p>
            <p>
              <v-btn color="primary" class="" :to="{ name: 'login', query: { redirect: 'renew-event' } }">Login Now</v-btn>
              <v-btn text small class="ms-2" color="primary" to="/account/passwordrecovery">{{$t('account.login.forgot-your-password')}}</v-btn>
            </p>
            <h4 class="mt-8">Lost access to your account?</h4>
            <p>In case you lost access to your account you can create a new account and start a new event.</p>
            <p>
              <v-btn outlined color="primary" class="" :to="{ name: 'register', query: { redirect: 'create-event' } }">Create Account</v-btn>
            </p>
          </div>
          <div v-else>
            <h3>Select Event</h3>
            <p>You can easily clone a previous event, or start a new event.</p>
            <v-radio-group v-model="mode">
              <v-radio value="CLONE" class="mb-4">
                <template v-slot:label>
                  <div>
                    <div><strong>Clone previous event</strong></div>
                    <div>Copy the configuration from a previously organized event over. You can still make changes afterwards.</div>
                  </div>
                </template>
              </v-radio>
              <v-radio value="NEW" class="mb-4">
                <template v-slot:label>
                  <div>
                    <div><strong>Create new</strong></div>
                    <div>Create a new event.</div>
                  </div>
                </template>
              </v-radio>
            </v-radio-group>   

            <v-sheet v-if="mode == 'CLONE'" class="admin-input-container ps-8 mb-8">
              <v-select  
                v-model="eventId"
                :items="events"
                label="Select Event"
                item-text="name"
                item-value="id"
                />
              <inline-tip v-if="!eventId">Can't find the event you want to clone? Please make sure you are logged in with the correct account. You can only clone paid events that you are an admin for.</inline-tip>

              <div v-if="selectedEvent" class="mb-4">
                <!-- <h4>{{ selectedEvent.name }}</h4> -->
                <p>Original dates: {{ selectedEvent.from | localDate }} - {{ selectedEvent.till | localDate }}.</p>
                <p>License: {{selectedEvent.max_participants}}x {{ selectedEvent.license | license }}.</p>
                <p>Support plan: {{ selectedEvent.support_plan | supportPlan }}.</p>

                <h4>Organization</h4>
                <p v-if="selectedEvent.org">Organization: {{ selectedEvent.org.name }}.</p>
                <div v-else>
                  <v-radio-group v-model="orgMode">
                    <v-radio
                      value="CREATE"
                      label="Create a new organization"
                      />
                    <v-text-field v-if="orgMode == 'CREATE'"
                      v-model="orgName"
                      label="Name of the organization"
                      class="ml-8"
                      :rules="orgRules"
                      />

                    <v-radio
                      value="LINK"
                      label="Link to an existing organization"
                      />
                    <v-select v-if="orgMode == 'LINK' && orgs"
                      v-model="selectedOrgId"
                      :items="orgs"
                      item-text="name"
                      item-value="id"
                      label="Select your organization"
                      class="ml-8"
                      :rules="orgRules"
                      />
                    <inline-tip v-if="selectedOrgId">The event will be linked to the selected organization. Organization admins will get access to this event, and the new edition automatically.</inline-tip>
                    <v-alert v-if="orgMode == 'LINK' && !selectedOrgId && (!orgs || !orgs.length)" type="info">No organization found. Please create a new organization instead.</v-alert>
                  </v-radio-group>    
                </div>

                <h4>Next Steps</h4>
                <p>On the next screen you can review the order, you can change the number of users and the support plan.</p>
                <p>After completing the payment you can clone the event and make changes to the configuration.</p>
              </div>                

            </v-sheet>
            <v-sheet v-if="mode == 'NEW'" class="admin-input-container ps-8 mb-8">
              You can create a new event on the next page.
            </v-sheet>

            <p>
              <v-btn color="primary" :disabled="!valid" @click="submit">{{ mode == 'NEW' ? 'Next' : 'Review Order' }}</v-btn>
            </p>           
          </div>
        </v-form>
      </v-card-text>
    </v-card>

  </div>      
</template>



<script>
import { mapGetters } from "vuex";
import orgManagerService from "@/services/orgManagerService";
import eventManagerService from "@/services/eventManagerService";
import { EventBus } from '@/plugins/eventbus.js';
import { DateTime } from 'luxon'
import PageHeader from '@/components/PageHeader.vue';
import siteData from '@/data/site.json'
import tenants from '@/data/tenants.config'
import InlineTip from '@/components/InlineTip.vue';
const tenant = tenants.current();

export default {
  name: "Profile",
  components: {
    PageHeader,
    InlineTip,
  },
  props: {
  },
  data() {
    return {
      tenant: tenant,
      siteData: siteData,
      mode: null,
      orgs: null,
      orgName: null,
      orgMode: 'CREATE',
      selectedOrgId: null,
      eventId: null,
      events: null,
      selectedEvent: null,
      orgRules: [
        v => !!v || 'Please enter the name of your organization',
      ],
      breadcrumbItems: [
        { text: 'All Events', exact: true, to: { name: 'eventmanager' } },
      ],

    };
  },
  async mounted() {
    
    EventBus.$on('login-state-change', async user => {
      await this.getProfile();
    });
    await this.getProfile();
  },
  methods: {
    async getProfile() {
      if (this.user && !this.profile) {
        this.events = (await eventManagerService.getOwnedEventsForRenewal()).data.data;
        this.orgs = (await orgManagerService.getOwnedEvents()).data.data;
      }
    },

    async submit() {
      if (this.mode == 'NEW') {
        this.$router.push({ name: "eventmanagerCreate" });
      }
      else if (this.selectedEvent && this.$refs.form.validate()) {
        var orgId;
        if (this.selectedEvent.org) {
          orgId = this.selectedEvent.org.id;
        }
        else if (this.orgMode == 'LINK' && this.selectedOrgId) {
          orgId = this.selectedOrgId;
          // link event to org so we can use the purchased license
          await eventManagerService.linkOrg(this.selectedEvent.id, this.selectedOrgId);
        }
        else {
          var response = await orgManagerService.create({
            name: this.orgName,
          });
          this.$analytics.event('create_org');
          orgId = response.data.id;

          // link event to org so we can use the purchased license
          await eventManagerService.linkOrg(this.selectedEvent.id, orgId);
        }

        this.$router.push({ name: "checkoutConfirm", query: { mode: 'event', license: this.selectedEvent.license, participants: this.selectedEvent.max_participants, support: this.selectedEvent.support_plan, redirect: 'clone-event', relatedEventId: this.selectedEvent.id, orgId: orgId } });
      }
    },
  },
  computed: {
    betaMode() {
      return process.env.NODE_ENV !== 'production';
    },
    valid() {
      return this.mode == 'NEW'
          || (this.mode == 'CLONE' && this.eventId);
    },
    ...mapGetters({
      user: "user"
    })
  },
  watch: {
    async eventId(value) {
      this.selectedEvent = null;
      this.selectedEvent = (await eventManagerService.get(value)).data;
    }
  }

};
</script>
<style lang="scss">
  </style>

